import { apiClient, API_ENDPOINTS } from '@/shared/apiClient';
import {
  ensureAuthRuntimeTokenStatus,
  getAuthRuntimeHeaders,
  hasAuthRuntimeProvider,
} from '@/features/auth/services/authRuntime';
import type {
  CreateAttachmentUploadInput,
  CreateAttachmentUploadResult,
  FeedbackAttachment,
  FeedbackAttachmentKind,
  FeedbackBackend,
  FeedbackComment,
} from '@dope-ai/feedback-mode';

export type FeedbackCapabilities = {
  read: boolean;
  create: boolean;
  updateOwn: boolean;
  resolve: boolean;
  manage: boolean;
  export: boolean;
};

export type FeedbackProviderState = {
  enabled: boolean;
  orgId: string;
  orgName: string;
  projectId: string;
  pageKey?: string | null;
  userId: string;
  membershipRole?: string | null;
  capabilities: FeedbackCapabilities;
  reasons: string[];
  packageName: string;
  backend: string;
};
export type { FeedbackComment };

export type FeedbackAdminSettings = {
  global: {
    enabled: boolean;
    featureCeiling: 'off' | 'comments' | 'full';
  };
  organizations: Array<{
    organizationId: string;
    name: string;
    slug: string;
    status: string;
    feedbackModeEnabled: boolean;
    feedbackFeatureCeiling: 'off' | 'comments' | 'full';
    members: FeedbackAdminMember[];
  }>;
};

export type FeedbackAdminMember = {
  userId: string;
  username: string;
  displayName: string;
  email?: string | null;
  accountRole?: string | null;
  membershipRole: 'platform_admin' | 'org_admin' | 'member';
  membershipStatus: 'active' | 'disabled';
  isActive: boolean;
  feedbackCapabilities: FeedbackCapabilities;
};

// The host initializes only the overlay. Keeping this as a narrow dynamic
// import lets Rollup omit the package's standalone export, attachment, token,
// selector, and store helpers from the production feedback chunk.
type FeedbackModePackage = Pick<typeof import('@dope-ai/feedback-mode/overlay'), 'initFeedbackMode'>;

export async function loadFeedbackModePackage(): Promise<FeedbackModePackage | null> {
  try {
    const { initFeedbackMode } = await import('@dope-ai/feedback-mode/overlay');
    return { initFeedbackMode };
  } catch {
    return null;
  }
}

const commentsEndpoint = (commentId: string): string =>
  `${API_ENDPOINTS.feedbackMode.comments}/${encodeURIComponent(commentId)}`;

const queryString = (params: Record<string, string | number | boolean | null | undefined>): string => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    query.set(key, String(value));
  }
  const encoded = query.toString();
  return encoded ? `?${encoded}` : '';
};

const hasAuthorizationHeader = (headers: HeadersInit): boolean => {
  if (headers instanceof Headers) {
    return Boolean(headers.get('authorization') || headers.get('Authorization'));
  }
  if (Array.isArray(headers)) {
    return headers.some(([key, value]) => key.toLowerCase() === 'authorization' && value.trim().length > 0);
  }
  const record = headers as Record<string, string | undefined>;
  return Boolean((record.Authorization || record.authorization || '').trim());
};

export const isFeedbackModeAuthReady = async (): Promise<boolean> => {
  if (!hasAuthRuntimeProvider()) {
    return false;
  }
  const status = await ensureAuthRuntimeTokenStatus().catch(() => 'transient');
  if (status !== 'valid') {
    return false;
  }
  return hasAuthorizationHeader(getAuthRuntimeHeaders());
};

const isAuthUnavailableError = (error: unknown): boolean => {
  const maybeStatus = Number((error as { status?: unknown })?.status);
  if (maybeStatus === 401 || maybeStatus === 403) {
    return true;
  }
  const message = error instanceof Error ? error.message : String(error || '');
  return (
    message.includes('Authentication required') ||
    message.includes('Session is no longer authorized') ||
    message.includes('Refresh token is missing') ||
    message.includes('API Error: Not authenticated') ||
    message.includes('API Error: Unauthorized')
  );
};

const authDisabledProvider = (projectId: string, pageKey: string): FeedbackProviderState => ({
  enabled: false,
  orgId: '',
  orgName: '',
  projectId,
  pageKey,
  userId: '',
  membershipRole: null,
  capabilities: {
    read: false,
    create: false,
    updateOwn: false,
    resolve: false,
    manage: false,
    export: false,
  },
  reasons: ['auth_not_ready'],
  packageName: '@dope-ai/feedback-mode',
  backend: 'management-tool-mongo',
});

async function readFeedbackOr<T>(fallback: T, operation: () => Promise<T>): Promise<T> {
  if (!(await isFeedbackModeAuthReady())) {
    return fallback;
  }
  try {
    return await operation();
  } catch (error) {
    if (isAuthUnavailableError(error)) {
      return fallback;
    }
    throw error;
  }
}

const attachmentKindForMime = (mimeType: string): FeedbackAttachmentKind => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.startsWith('video/')) return 'video';
  if (
    mimeType.includes('word') ||
    mimeType.includes('document') ||
    mimeType.includes('hwp') ||
    mimeType.includes('spreadsheet') ||
    mimeType.includes('presentation')
  ) {
    return 'document';
  }
  return 'file';
};

const normalizeAttachment = (
  raw: Record<string, unknown>,
  input: CreateAttachmentUploadInput,
): FeedbackAttachment => {
  const mimeType = String(raw.contentType || raw.mimeType || input.mimeType || 'application/octet-stream');
  const sizeBytes = Number(raw.sizeBytes || raw.size || input.sizeBytes || 0);
  const uploadedByUserId = String(raw.uploadedByUserId || '');
  return {
    id: String(raw.id || `attachment-${Date.now()}`),
    kind: attachmentKindForMime(mimeType),
    fileName: String(raw.fileName || raw.filename || input.fileName),
    mimeType,
    sizeBytes,
    storageKey: typeof raw.storageKey === 'string' ? raw.storageKey : undefined,
    url: typeof raw.url === 'string' ? raw.url : undefined,
    thumbnailUrl: typeof raw.thumbnailUrl === 'string' ? raw.thumbnailUrl : undefined,
    uploadedByUserId,
    uploadedByName: String(raw.uploadedByName || raw.ownerName || uploadedByUserId || 'Unknown'),
    createdAt: String(raw.createdAt || new Date().toISOString()),
  };
};

export function createManagementToolFeedbackBackend(onCommentsChanged?: () => void | Promise<void>): FeedbackBackend {
  const notifyCommentsChanged = () => {
    try {
      // Presence refresh is best-effort; a committed write must keep its result.
      void Promise.resolve(onCommentsChanged?.()).catch(() => undefined);
    } catch {
      // Synchronous observers are isolated for the same reason.
    }
  };
  return {
    kind: 'management-tool-mongo',
    persistent: true,
    async list(params) {
      return readFeedbackOr(
        [],
        () => apiClient.get<FeedbackComment[]>(
          `${API_ENDPOINTS.feedbackMode.comments}${queryString({
            orgId: params.orgId,
            projectId: params.projectId,
            pageKey: params.pageKey,
            status: params.status,
            ownerUserId: params.ownerUserId,
          })}`,
        ),
      );
    },
    async create(input) {
      const comment = await apiClient.post<FeedbackComment>(API_ENDPOINTS.feedbackMode.comments, input);
      notifyCommentsChanged();
      return comment;
    },
    async update(id, input) {
      const comment = await apiClient.patch<FeedbackComment>(commentsEndpoint(id), input);
      notifyCommentsChanged();
      return comment;
    },
    async requestConfirmation(id, input) {
      const comment = await apiClient.post<FeedbackComment>(`${commentsEndpoint(id)}/request-confirmation`, input || {});
      notifyCommentsChanged();
      return comment;
    },
    async confirm(id, input) {
      const comment = await apiClient.post<FeedbackComment>(`${commentsEndpoint(id)}/confirm`, input || {});
      notifyCommentsChanged();
      return comment;
    },
    async remove(id) {
      await apiClient.delete<Record<string, never>>(commentsEndpoint(id));
      notifyCommentsChanged();
    },
    async exportMarkdown(params) {
      return apiClient.get<string>(
        `${API_ENDPOINTS.feedbackMode.export}${queryString({
          orgId: params.orgId,
          projectId: params.projectId,
          pageKey: params.pageKey,
          format: params.format || 'markdown',
        })}`,
      );
    },
    attachments: {
      acceptsDataUrl: true,
      async createUpload(input): Promise<CreateAttachmentUploadResult> {
        const raw = await uploadFeedbackAttachment({
          orgId: input.orgId,
          projectId: input.projectId,
          commentId: input.scope.targetType === 'comment' ? input.scope.targetId : undefined,
          filename: input.fileName,
          contentType: input.mimeType,
          dataUrl: input.dataUrl,
          metadata: {
            feedbackModeScope: input.scope,
          },
        });
        return {
          attachment: normalizeAttachment(raw, input),
        };
      },
    },
  };
}

export async function getFeedbackProvider(projectId: string, pageKey: string): Promise<FeedbackProviderState> {
  return readFeedbackOr(authDisabledProvider(projectId, pageKey), () => {
    const params = new URLSearchParams({ projectId, pageKey });
    return apiClient.get<FeedbackProviderState>(`${API_ENDPOINTS.feedbackMode.provider}?${params.toString()}`);
  });
}

export async function listFeedbackComments(
  orgId: string,
  projectId: string,
  pageKey: string,
): Promise<FeedbackComment[]> {
  return readFeedbackOr([], () => {
    const params = new URLSearchParams({ orgId, projectId, pageKey });
    return apiClient.get<FeedbackComment[]>(`${API_ENDPOINTS.feedbackMode.comments}?${params.toString()}`);
  });
}

export async function uploadFeedbackAttachment(payload: {
  orgId: string;
  projectId: string;
  pageKey?: string;
  commentId?: string;
  filename: string;
  contentType?: string;
  dataUrl?: string;
  metadata?: Record<string, unknown>;
}): Promise<Record<string, unknown>> {
  return apiClient.post<Record<string, unknown>>(API_ENDPOINTS.feedbackMode.attachmentUploads, payload);
}

export async function getFeedbackAdminSettings(): Promise<FeedbackAdminSettings> {
  return apiClient.get<FeedbackAdminSettings>(API_ENDPOINTS.admin.feedbackMode.settings);
}

export async function updateFeedbackGlobal(enabled: boolean): Promise<unknown> {
  return apiClient.put(API_ENDPOINTS.admin.feedbackMode.global, {
    enabled,
    featureCeiling: enabled ? 'full' : 'off',
  });
}

export async function updateFeedbackOrganization(organizationId: string, enabled: boolean): Promise<unknown> {
  return apiClient.put(API_ENDPOINTS.admin.feedbackMode.organization(organizationId), {
    enabled,
    featureCeiling: enabled ? 'full' : 'off',
  });
}

export async function updateFeedbackUserCapabilities(
  userId: string,
  capabilities: FeedbackCapabilities,
  organizationId?: string,
): Promise<unknown> {
  return apiClient.put(API_ENDPOINTS.admin.feedbackMode.userCapabilities(userId), {
    capabilities,
    ...(organizationId ? { organizationId } : {}),
  });
}
